import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { BookmarkAddedOutlined } from "@mui/icons-material";
import { assistantsService, AiMemoryItem } from "../services/assistantsService";
import { colors } from "../theme/designSystem";

// Regras gravadas pela equipe para os assistentes (RN-577): conta do extrato, categoria por pagador e regras em texto
// do Livro-caixa. Ver, corrigir o texto e excluir. Excluir faz a regra deixar de valer; categoria e conta se corrigem
// excluindo — o assistente pergunta de novo e a equipe ensina o certo pelo cartão.

interface Props {
  onToast: (message: string, type: "success" | "error") => void;
}

const MAX_TEXT = 2000;

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { description?: string } } })?.response?.data?.description || fallback;

const updatedLabel = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : `atualizada em ${d.toLocaleDateString("pt-BR")}`;
};

const MemoryRow: React.FC<{ item: AiMemoryItem; onChanged: () => void; onToast: Props["onToast"] }> = ({ item, onChanged, onToast }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.then);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await assistantsService.updateMemoryText(item.id, text.trim());
      onToast("Regra atualizada.", "success");
      setEditing(false);
      onChanged();
    } catch (e) {
      onToast(errorText(e, "Não foi possível salvar a regra."), "error");
    } finally {
      setBusy(false);
    }
  };

  const forget = async () => {
    setBusy(true);
    try {
      await assistantsService.forgetMemory(item.id);
      onToast("Regra excluída: os assistentes deixam de usá-la.", "success");
      onChanged();
    } catch (e) {
      onToast(errorText(e, "Não foi possível excluir a regra."), "error");
      setBusy(false);
    }
  };

  return (
    <Box component="li" sx={{ py: 1.75, borderBottom: `1px solid ${colors.backgroundSubtle}`, display: "grid", gap: 0.5,
      gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1.2fr) auto" }, columnGap: 3, alignItems: "start" }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: colors.textSecondary }}>{item.kindLabel}</Typography>
        <Typography sx={{ fontSize: "0.875rem", color: colors.textStrong, overflowWrap: "anywhere" }}>{item.when}</Typography>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        {editing ? (
          <TextField fullWidth multiline minRows={2} maxRows={8} size="small" value={text} onChange={(e) => setText(e.target.value)}
            inputProps={{ maxLength: MAX_TEXT, "aria-label": `Texto da regra ${item.when}` }} />
        ) : (
          <Typography sx={{ fontSize: "0.875rem", color: colors.textPrimary, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{item.then}</Typography>
        )}
        <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary, mt: 0.25 }}>{updatedLabel(item.updatedAt)}</Typography>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: { xs: "flex-start", md: "flex-end" }, mt: { xs: 0.5, md: 0 } }}>
        {busy && <CircularProgress size={18} aria-label="Salvando" />}
        {!busy && editing && (
          <>
            <Button variant="contained" size="small" onClick={save} disabled={!text.trim() || text.trim() === item.then}
              sx={{ minHeight: 36, textTransform: "none", boxShadow: "none" }}>Salvar</Button>
            <Button size="small" onClick={() => { setEditing(false); setText(item.then); }} sx={{ minHeight: 36, textTransform: "none", color: colors.textPrimary }}>Cancelar</Button>
          </>
        )}
        {!busy && !editing && confirming && (
          <>
            <Button variant="outlined" color="error" size="small" onClick={forget} sx={{ minHeight: 36, textTransform: "none" }}>Excluir regra</Button>
            <Button size="small" onClick={() => setConfirming(false)} sx={{ minHeight: 36, textTransform: "none", color: colors.textPrimary }}>Manter</Button>
          </>
        )}
        {!busy && !editing && !confirming && (
          <>
            {item.editable && (
              <Button size="small" onClick={() => setEditing(true)} sx={{ minHeight: 36, textTransform: "none", color: colors.primaryText }}>Editar</Button>
            )}
            <Button size="small" onClick={() => setConfirming(true)} sx={{ minHeight: 36, textTransform: "none", color: colors.textPrimary }}>Excluir</Button>
          </>
        )}
      </Box>
    </Box>
  );
};

const AssistantMemoriesConfig: React.FC<Props> = ({ onToast }) => {
  const [items, setItems] = useState<AiMemoryItem[] | null>(null);

  const load = useCallback(() => {
    assistantsService.memories()
      .then(setItems)
      .catch(() => { setItems([]); onToast("Não foi possível carregar as regras gravadas.", "error"); });
  }, [onToast]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box component="section" aria-labelledby="ai-memories-title" sx={{ mt: 4, pt: 3, borderTop: "1px solid #e9ecef" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <BookmarkAddedOutlined sx={{ color: colors.primary }} aria-hidden />
        <Typography id="ai-memories-title" sx={{ fontWeight: 600, color: colors.textPrimary }}>Regras gravadas pelos assistentes</Typography>
      </Box>
      <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1.5, maxWidth: "56rem" }}>
        O que a equipe ensinou aos assistentes: a conta de um extrato, a categoria de um pagador no Livro-caixa e regras em texto.
        Valem para toda a equipe desta unidade. Só se gravam quando alguém confirma — marcando <strong>Lembrar esta conta</strong> ou
        no cartão <strong>Gravar regra</strong>. Para corrigir uma categoria ou conta, exclua: o assistente pergunta de novo na próxima vez.
      </Typography>

      {items === null && <CircularProgress size={22} aria-label="Carregando as regras" />}
      {items !== null && items.length === 0 && (
        <Typography sx={{ fontSize: "0.875rem", color: colors.textSecondary }}>Nenhuma regra gravada ainda.</Typography>
      )}
      {items !== null && items.length > 0 && (
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, maxWidth: "72rem", borderTop: `1px solid ${colors.backgroundSubtle}` }}>
          {items.map((item) => <MemoryRow key={`${item.id}:${item.updatedAt}`} item={item} onChanged={load} onToast={onToast} />)}
        </Box>
      )}
    </Box>
  );
};

export default AssistantMemoriesConfig;
